import * as React from 'react';
import { Container, Offcanvas } from 'react-bootstrap';
import { Outlet } from 'react-router-dom';
import { addUrlPrefixToSearch, ErrorBoundary, SearchLinkProps, useActiveItem } from 'gossamer-front-end';
import {
  Event,
  FeedbackForm,
  Institution,
  InstitutionGroup,
  InstitutionMemberInvite,
  InstitutionProposal,
  Location,
  LocationCheckLog,
  LocationTrackingSchedule,
  OutboundEmail,
  OrgUnit,
  PaymentIn,
  PaymentOut,
  PushSubscriptionHealth,
  UserView,
} from 'cb-model';
import { Sidebar } from './Sidebar';
import { Transaction } from 'gossamer-model';
import classes from './SidebarContainer.module.css';

interface Props {
  children?: React.ReactNode;
}

const links: Record<string, SearchLinkProps[]> = {
  main: [
    {
      entity: Institution,
      urlPath: '/institution/search',
      urlPrefix: '/institution/',
      roles: ['staff'],
    },
    {
      entity: Event,
      urlPath: '/event/search',
      urlPrefix: '/event/',
    },
    {
      entity: UserView,
      urlPath: '/user/search',
      urlPrefix: '/user/',
      roles: ['staff'],
    },
    {
      entity: InstitutionMemberInvite,
      urlPath: '/institution-member/search',
      label: 'Member Invites',
    },
    {
      entity: UserView,
      urlPath: '/institution-member/search-unverified',
      label: 'Followers',
    },
    {
      entity: UserView,
      urlPath: '/institution-member/search-verified',
      label: 'Members',
    },
    {
      entity: UserView,
      urlPath: '/institution-member/search-admin',
      label: 'Admins',
    },
    {
      entity: InstitutionGroup,
      urlPath: '/institution-group/search',
    },
    {
      entity: OrgUnit,
      urlPath: '/org-unit/search',
    },
    {
      entity: InstitutionProposal,
      urlPath: '/institution-proposal/search',
    },
    {
      entity: FeedbackForm,
      urlPath: '/feedback-form/search',
    },
    {
      entity: Location,
      urlPath: '/location/search',
      urlPrefix: '/location/',
      roles: ['staff'],
    },
    {
      entity: LocationTrackingSchedule,
      urlPath: '/location-tracking-schedule/search',
      urlPrefix: '/location-tracking-schedule/',
      roles: ['staff'],
    },
    {
      entity: LocationCheckLog,
      urlPath: '/location-check-log/search',
      urlPrefix: '/location-check-log/',
      roles: ['staff'],
    },
    {
      entity: OutboundEmail,
      urlPath: '/outbound-email/search',
    },
    {
      entity: PaymentIn,
      urlPath: '/payments-in/search',
      label: 'Payments In',
      roles: ['staff'],
    },
    {
      entity: PaymentOut,
      urlPath: '/payments-out/search',
      label: 'Payments Out',
      roles: ['staff'],
    },
    {
      entity: PushSubscriptionHealth,
      urlPath: '/push-notification/diagnostics',
      label: 'Push Notification Diagnostics',
      roles: ['staff'],
    },
    {
      entity: Transaction,
      urlPath: '/transaction/search',
    },
  ],
};

Object.values(links).forEach((section) =>
  section.forEach((link) => link.urlPrefix && addUrlPrefixToSearch(link.urlPrefix, link.urlPath, link.entity)),
);

export const SidebarContainer = (props: Props): React.JSX.Element => {
  useActiveItem(links);
  const [showMobile, setShowMobile] = React.useState(false);
  const handleClose = () => setShowMobile(false);
  const toggleClose = () => setShowMobile(!showMobile);
  return (
    <ErrorBoundary>
      <div className={classes.ToggleContainer}>
        <img src="/assets/icons/bars3.svg" onClick={toggleClose} />
      </div>
      <div className={classes.SidebarContainer}>
        <Offcanvas show={showMobile} onHide={handleClose} responsive="lg" placement="end">
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>Admin Menu</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Sidebar onClose={handleClose} />
          </Offcanvas.Body>
        </Offcanvas>
        <main>
          <Outlet />
        </main>
      </div>
    </ErrorBoundary>
  );
};
